import { formatMoneyShort } from "@/components/Plan/MoneyInput";
import { formatMoneyShortBare } from "@/lib/money";

interface MonthCell {
  period_date: string;
  quantity: number;
  actual_quantity: number | null;
  actual_revenue?: number | null;
  actual_gm: number | null;
}

interface Props {
  cell: MonthCell;
  targetAsp: number | null;
  cogs: number | null;
  currency?: string | null;
  canViewGrossMargin: boolean;
}

// Actual GM within 2 percentage points below target still shows as warning,
// not miss; beyond that it's a real miss.
const GM_WARNING_BAND = 0.02;

function formatPct(actual: number, plan: number): string {
  const pct = Math.round((actual / plan) * 100);
  // Outlier attainment (tiny plan, real sales) clamps — exact value is in the
  // tooltip; 4+ digits only steal width from the value pair.
  return pct > 999 ? ">999%" : `${pct}%`;
}

// Units in the actual/plan pair: no thousands separators, and K-compact from
// 10,000 up — the cell is width-constrained and exact counts live in the
// tooltip.
function formatUnits(n: number): string {
  if (n >= 10_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

// Tooltip for a past cell in Plan-vs-Actual mode: units attainment, real
// revenue, realized-vs-target ASP (the price-variance bridge), and GM
// attainment when visible.
function actualsTooltip(
  cell: MonthCell,
  targetAsp: number | null,
  targetGm: number | null,
  currency?: string | null,
): string {
  const actUnits = cell.actual_quantity ?? 0;
  const parts: string[] = [];
  let units = `Actual: ${actUnits.toLocaleString("en-US")} units`;
  if (cell.quantity > 0) units += ` (${formatPct(actUnits, cell.quantity)} of plan)`;
  parts.push(units);
  if (cell.actual_revenue != null && cell.actual_revenue > 0) {
    parts.push(`${formatMoneyShort(cell.actual_revenue, currency)} revenue`);
    if (actUnits > 0) {
      const realizedAsp = cell.actual_revenue / actUnits;
      let asp = `realized ASP ${formatMoneyShort(realizedAsp, currency)}`;
      if (targetAsp) asp += ` vs target ${formatMoneyShort(targetAsp, currency)}`;
      parts.push(asp);
    }
  }
  if (cell.actual_gm != null && targetGm != null) {
    parts.push(`GM ${Math.round(cell.actual_gm * 100)}% vs target ${Math.round(targetGm * 100)}%`);
  }
  return parts.join(" · ");
}

// Attainment bar with the % beside it on the same line, so the value pair
// above never shares its row (and its width) with the percentage.
export function AttainmentLine({ actual, plan }: { actual: number; plan: number }) {
  return (
    <span className="flex items-center gap-1.5 mt-0.5">
      <span className="block flex-1 min-w-0">
        <AttainmentBar actual={actual} plan={plan} />
      </span>
      <span
        className="shrink-0 text-[10px] leading-none font-normal tabular-nums"
        style={{ color: "var(--muted-foreground)" }}
      >
        {formatPct(actual, plan)}
      </span>
    </span>
  );
}

export function AttainmentBar({ actual, plan }: { actual: number; plan: number }) {
  return (
    <span
      className="block h-0.5 rounded-full overflow-hidden"
      style={{ background: "color-mix(in srgb, var(--foreground) 6%, transparent)" }}
    >
      <span
        className="block h-full"
        style={{
          width: `${Math.min(actual / plan, 1) * 100}%`,
          background:
            actual >= plan
              ? "color-mix(in srgb, var(--success) 45%, transparent)"
              : "color-mix(in srgb, var(--primary) 40%, transparent)",
        }}
      />
    </span>
  );
}

// Plan-vs-Actual past cell: an actual/plan units line and an actual/plan
// revenue line (currency symbol dropped — unambiguous from context), each with
// % of plan and a hairline attainment bar, plus a per-month GM status dot on
// the revenue line. Both blocks always render with fixed heights (h-4 rows +
// bar spacers) so the bottom-anchored row labels in the product column line up
// across sparse and full cells.
export default function ActualsCell({ cell, targetAsp, cogs, currency, canViewGrossMargin }: Props) {
  const planQty = cell.quantity;
  const planRevenue = (targetAsp ?? 0) * planQty;
  const actualQty = cell.actual_quantity ?? 0;
  const actualRevenue = cell.actual_revenue ?? 0;

  const targetGm =
    targetAsp && targetAsp > 0 && cogs != null ? (targetAsp - cogs) / targetAsp : null;
  const gmStatus =
    canViewGrossMargin && cell.actual_gm != null && targetGm != null
      ? cell.actual_gm >= targetGm
        ? "var(--success)"
        : cell.actual_gm >= targetGm - GM_WARNING_BAND
          ? "var(--warning)"
          : "var(--destructive)"
      : null;

  return (
    <div className="px-1.5 pt-1.5 pb-1 relative" title={actualsTooltip(cell, targetAsp, targetGm, currency)}>
      {gmStatus && (
        <span
          aria-label="Gross margin vs target"
          className="absolute top-0 right-0 h-1.5 w-1.5 rounded-full"
          style={{ background: gmStatus }}
        />
      )}
      <div>
        <div
          style={{ color: "var(--muted-foreground)" }}
          className="flex items-baseline gap-1 text-[10px] leading-4 h-4 tabular-nums"
        >
          <span className="flex-1 text-left">
            {(planQty > 0 || actualQty > 0) && (
              <>
                <span style={{ color: "var(--foreground)" }}>{formatUnits(actualQty)}</span>
                {planQty > 0 && <>/{formatUnits(planQty)}</>}
              </>
            )}
          </span>
          {planQty > 0 && <span>{formatPct(actualQty, planQty)}</span>}
        </div>
        {planQty > 0 ? (
          <AttainmentBar actual={actualQty} plan={planQty} />
        ) : (
          <div className="h-0.5" />
        )}
      </div>

      <div className="mt-2">
        <div
          style={{ color: "var(--muted-foreground)" }}
          className="flex items-baseline gap-1 text-[10px] leading-4 h-4 tabular-nums"
        >
          <span className="flex-1 text-left">
            {(planRevenue > 0 || actualRevenue > 0) && (
              <>
                <span style={{ color: "var(--foreground)" }}>
                  {formatMoneyShortBare(actualRevenue, currency)}
                </span>
                {planRevenue > 0 && <>/{formatMoneyShortBare(planRevenue, currency)}</>}
              </>
            )}
          </span>
          {planRevenue > 0 && <span>{formatPct(actualRevenue, planRevenue)}</span>}
        </div>
        {planRevenue > 0 ? (
          <AttainmentBar actual={actualRevenue} plan={planRevenue} />
        ) : (
          <div className="h-0.5" />
        )}
      </div>
    </div>
  );
}
